/**
 * Shared API contracts.
 *
 * These are the shapes the *application* works with. They are deliberately not
 * the shapes the UT system backend returns — see `backend-envelopes.ts` for
 * those and for the adapters that bridge the two.
 *
 * Keeping the two apart is what stops an accidental backend response structure
 * (a misspelled `isAutherized`, a `PaginationMetadata.currentPage`) leaking
 * into 50 components (§14).
 */

/** Every list endpoint in this app resolves to this shape. */
export interface PagedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationRequest {
  page: number;
  pageSize: number;
}

export type SortDirection = 'asc' | 'desc';

export interface SortingRequest {
  sort?: string;
  direction?: SortDirection;
}

/** The query shape shared by every server-driven table. */
export interface ListParams extends PaginationRequest, SortingRequest {
  search?: string;
}

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/**
 * Build a `PagedResponse` from a full, unpaged array.
 *
 * Most UT system list endpoints (`/api/Brands`, `/api/Vendors`, `/api/Users`,
 * `/api/Orders/report`, …) return the entire table with no paging support at
 * all — only 3 of ~40 endpoints page. Until the backend grows real paging,
 * the API layer slices client-side so that the rest of the app can be written
 * against one honest contract.
 *
 * This is a bridge, not a destination: it still transfers every row over the
 * wire. Endpoints expected to grow past a few thousand rows need server paging
 * added backend-side — see docs/architecture.md "Pagination".
 */
export function paginateLocally<T>(
  items: readonly T[],
  { page, pageSize }: PaginationRequest,
): PagedResponse<T> {
  const totalItems = items.length;
  const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / pageSize);
  const start = (page - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize),
    page,
    pageSize,
    totalItems,
    totalPages,
  };
}
